import { useEffect, useRef, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Button, { TooltipButton } from "@/v2-backoffice-client/components/button"
import { useLayerAiAssistMutation } from "@/v2-backoffice-client/layer/ai/layer.ai.mutation"
import LayerAiThread from "@/v2-backoffice-client/layer/ai/LayerAiThread"
import LayerAiComposer from "@/v2-backoffice-client/layer/ai/LayerAiComposer"
import LayerAiIdentityCard from "@/v2-backoffice-client/layer/ai/LayerAiIdentityCard"
import LayerAiSuggestionPanel from "@/v2-backoffice-client/layer/ai/LayerAiSuggestionPanel"
import { IS_CATALOG_AUTHORING_ENABLED } from "@/v2-backoffice-client/feature/feature.constants"
import {
  takeLayerAiHandoff,
  type LayerAiChatTurn,
  type LayerAiMode,
} from "@/v2-backoffice-client/layer/ai/layer.ai.handoff"
import type {
  LayerAiExternalPrompt,
  LayerAiFieldSuggestion,
  LayerAiForkProposal,
  LayerAiIdentityProposal,
  LayerAiSuggestableField,
} from "@/v2-backoffice-client/layer/ai/layer.ai.types"

/**
 * Assistant history is replayed to the model each turn. Suggested blobs are
 * NOT replayed verbatim (they are large, and the layer context — included
 * fresh in every request — already reflects applied/saved ones); this marker
 * tells the model which fields it proposed so unchanged context reads as
 * "the operator did not take it".
 */
function suggestionMarker(fields: string[]) {
  return `\n\n[You proposed replacements for: ${fields.join(", ")}. Whether they were applied is visible in the current draft/blobs.]`
}

const OPENING_MESSAGES: Record<LayerAiMode, string> = {
  quick: "Review the current setup and suggest concrete improvements.",
  wizard:
    "What business use cases could this layer source fulfill? Give me some ideas to react to.",
}

const CREATE_OPENING_MESSAGES: Record<LayerAiMode, string> = {
  quick:
    "Study this source and propose a complete first setup (identity included) for the strongest business case it supports.",
  wizard:
    "What business use cases could a new layer on this source fulfill? Give me some ideas to react to.",
}

/**
 * Shown when a turn carries suggestions but the model returned no ideas
 * (it happens despite the contract): pending proposals must never strand
 * the operator without a clickable way forward.
 */
const FALLBACK_IDEAS = [
  "Tune the styling",
  "Suggest filter controls",
  "Try another business case",
]

export type LayerAiAssistantProps = {
  className?: string
  /**
   * Identifies the layer for the server-side context build; omit in create
   * mode, where the layer does not exist yet.
   */
  featureCode?: string
  /**
   * Create mode: the probed layer-server url — the assistant's context is
   * the source service instead of a catalog row, and replies may carry an
   * identity proposal.
   */
  sourceUrl?: string
  /** Display name of the layer; prefills the manual fork proposal. */
  layerName?: string
  /** Auto-sends on arrival, starting a quick session if none is running. */
  externalPrompt?: LayerAiExternalPrompt | null
  /** Set for studio rows: the layer resolves from the authoring catalog. */
  environment?: string
  /** Current saved blobs — the diff base for non-Configuration suggestions. */
  blobs: Partial<Record<LayerAiSuggestableField, unknown>>
  /** Current Configuration editor draft. */
  draft: string
  /** Replaces the Configuration editor content with the accepted suggestion. */
  onApply(configuration: string): void
  /** Persists an accepted non-Configuration suggestion. */
  onSaveField(field: LayerAiSuggestableField, value: unknown): Promise<unknown>
  isSavingField?: boolean
  /** Create mode: applies a proposed identity into the create form. */
  onIdentity?(identity: LayerAiIdentityProposal): void
  /**
   * Mirrors the conversation upward after every turn, so the create sheet
   * can hand it to the created layer's assistant (see layer.ai.handoff).
   */
  onTurns?(turns: LayerAiChatTurn[]): void
  /**
   * The history replayed values (rewind/re-apply): the host refreshes
   * anything mirroring the documents, e.g. the preview map.
   */
  onHistoryNavigated?(): void
  /** Renders an X in the panel header that closes the panel. */
  onClose?(): void
}

/**
 * The AI co-pilot panel of the Configuration workbench. Two entry points,
 * one dialogue: "Quick suggestions" asks for an immediate improvement pass;
 * "Ideate use cases" (wizard) has the model propose business cases for the
 * layer source and converges through chat. Every reply can carry full
 * replacements for any of the layer's blobs, all tailored to one business
 * case; the review flow (diffs, apply/dismiss, history, forking) lives in
 * LayerAiSuggestionPanel — this host owns the conversation and its single
 * assist mutation.
 */
function LayerAiAssistant(props: LayerAiAssistantProps) {
  const {
    featureCode,
    sourceUrl,
    layerName,
    environment,
    blobs,
    draft,
    externalPrompt,
    onApply,
    onSaveField,
    isSavingField,
    onIdentity,
    onTurns,
    onHistoryNavigated,
    onClose,
    className,
  } = props

  // Create mode = no catalog row yet: the exchange runs against the source
  // url, fork proposals make no sense, and identity proposals appear.
  const isCreate = !featureCode

  const [mode, setMode] = useState<LayerAiMode | null>(null)
  const [turns, setTurns] = useState<LayerAiChatTurn[]>([])
  const [ideas, setIdeas] = useState<string[]>([])
  const [suggestions, setSuggestions] = useState<LayerAiFieldSuggestion[]>([])
  const [changeSummary, setChangeSummary] = useState<string | null>(null)
  const [fork, setFork] = useState<LayerAiForkProposal | null>(null)
  const [identity, setIdentity] = useState<LayerAiIdentityProposal | null>(null)

  const [assist, { loading }] = useLayerAiAssistMutation()

  // A fork hands its conversation over (see layer.ai.handoff): resume it so
  // the operator keeps working in the same context on the fork's sheet.
  useEffect(() => {
    if (!featureCode) return
    const inherited = takeLayerAiHandoff(featureCode)
    if (!inherited) return
    setMode(inherited.mode)
    setTurns(inherited.turns)
  }, [featureCode])

  // Pushed-in messages (diagnostics' "Fix with AI") send themselves: into
  // the running conversation when there is one, otherwise as the opening
  // message of a quick session. The id ref makes each push one-shot.
  const handledExternalPromptId = useRef<number | null>(null)
  useEffect(() => {
    if (!externalPrompt) return
    if (handledExternalPromptId.current === externalPrompt.id) return
    handledExternalPromptId.current = externalPrompt.id
    if (!mode) setMode("quick")
    void send(externalPrompt.text, mode ?? "quick", turns)
    // send/mode/turns are deliberately not dependencies: only a new push
    // may trigger a send.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPrompt])

  function updateTurns(next: LayerAiChatTurn[]) {
    setTurns(next)
    onTurns?.(next)
  }

  async function send(
    text: string,
    activeMode: LayerAiMode,
    history: LayerAiChatTurn[],
  ) {
    const nextTurns: LayerAiChatTurn[] = [
      ...history,
      { role: "user", content: text },
    ]
    updateTurns(nextTurns)
    setIdeas([])

    let result
    try {
      result = await assist({
        variables: {
          input: {
            FeatureCode: featureCode,
            Environment: environment,
            SourceUrl: isCreate ? sourceUrl : undefined,
            DraftConfiguration: draft,
            // Create mode is one server-side mode; quick/wizard only shape
            // the OPENING message and the header label locally.
            Mode: isCreate ? "create" : activeMode,
            // Error bubbles are thread history for the operator, not
            // conversation — never replayed to the model.
            Messages: nextTurns
              .filter((turn) => !turn.isError)
              .map((turn) => ({
                Role: turn.role,
                Content:
                  turn.role === "assistant" && turn.suggestedFields?.length
                    ? turn.content + suggestionMarker(turn.suggestedFields)
                    : turn.content,
              })),
          },
        },
      })
    } catch (assistError) {
      // Failures land in the thread (a toast vanishes before it is read);
      // the failed user message stays, so sending again just retries.
      updateTurns([
        ...nextTurns,
        {
          role: "assistant",
          isError: true,
          content:
            assistError instanceof Error
              ? assistError.message
              : "The assistant request failed.",
        },
      ])
      return
    }

    const answer = result.data?.layerAiAssist
    if (!answer) return

    updateTurns([
      ...nextTurns,
      {
        role: "assistant",
        content: answer.Reply,
        suggestedFields: answer.Suggestions.map(
          (suggestion) => suggestion.Field,
        ),
      },
    ])
    setIdeas(answer.Ideas)
    // Deliberately KEEP the current suggestions on a conversation-only turn:
    // an empty set means "no new proposals", not "withdraw the pending ones" —
    // asking the assistant a follow-up question must not wipe the operator's
    // in-review apply/dismiss list. A new non-empty set replaces it.
    if (answer.Suggestions.length > 0) {
      setSuggestions(answer.Suggestions)
      setChangeSummary(answer.ChangeSummary ?? null)
    }
    // Fork proposals need the authoring catalog; on prod deployments the
    // card is suppressed and the suggestions stay applicable in place.
    setFork(
      !isCreate && IS_CATALOG_AUTHORING_ENABLED ? (answer.Fork ?? null) : null,
    )
    // Same keep-on-silence rule as suggestions, for the identity proposal.
    if (isCreate && answer.Identity) {
      setIdentity(answer.Identity)
    }
  }

  function start(nextMode: LayerAiMode) {
    setMode(nextMode)
    const opening = isCreate ? CREATE_OPENING_MESSAGES : OPENING_MESSAGES
    void send(opening[nextMode], nextMode, [])
  }

  // The model is instructed to always ship ideas alongside suggestions,
  // but when it doesn't, generic refinement chips keep the flow clickable.
  const shownIdeas =
    ideas.length > 0 ? ideas : suggestions.length > 0 ? FALLBACK_IDEAS : []

  function reset() {
    setMode(null)
    updateTurns([])
    setIdeas([])
    setSuggestions([])
    setChangeSummary(null)
    setFork(null)
    setIdentity(null)
  }

  return (
    <section
      className={cn(
        "layer-ai-assistant flex min-h-0 flex-col gap-2",
        className,
      )}
    >
      {/* Styled like the editor's toolbar bar (see .editor-actions), so the
          workbench panels read as one family of surfaces. */}
      <header className="flex shrink-0 items-center gap-1 rounded-md border bg-muted/20 px-1.5 py-1">
        <Icon src="sparkles" size={13} className="ml-1 shrink-0 text-primary" />
        <span className="min-w-0 truncate text-sm font-medium">
          AI assistant
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">
            —{" "}
            {mode
              ? isCreate
                ? "shaping the new layer"
                : mode === "quick"
                  ? "quick suggestions"
                  : "ideating use cases"
              : isCreate
                ? "grounded in the probed source"
                : "grounded in this layer and its live service"}
          </span>
        </span>
        <span className="flex-1" />
        {mode && (
          <TooltipButton
            variant="ghost"
            size="sm"
            iconSrc="refresh"
            // The refresh glyph fills its viewbox; a notch smaller keeps it
            // optically level with cross-small next door.
            iconSize={11}
            content="Start over — clears the conversation, its suggestions and the applied-changes history"
            aria-label="Start over"
            data-feature-code="UI_BackofficeLayers_AiAssistant_StartOver"
            onClick={reset}
          />
        )}
        {onClose && (
          <TooltipButton
            variant="ghost"
            size="sm"
            iconSrc="cross-small"
            content="Close the AI assistant"
            aria-label="Close the AI assistant"
            data-feature-code="UI_BackofficeLayers_AiAssistant_Close"
            onClick={onClose}
          />
        )}
      </header>

      {!mode && (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-md border p-4 text-center">
          <Icon src="sparkles" size={24} className="text-muted-foreground" />
          <p className="max-w-sm text-xs text-muted-foreground">
            {isCreate
              ? "The assistant reads the source service (fields, geometry, existing layers on it) and can propose the whole layer: identity, styling, filters, table and export — tailored to a business case."
              : "The assistant knows this layer's metadata, the live service fields, and your current draft — and can propose the full setup (styling, filters, table, export) for a business case."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              size="sm"
              iconSrc="bolt"
              data-feature-code="UI_BackofficeLayers_AiAssistant_Quick"
              onClick={() => start("quick")}
            >
              {isCreate ? "Suggest a setup" : "Quick suggestions"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              iconSrc="lightbulb-on"
              data-feature-code="UI_BackofficeLayers_AiAssistant_Wizard"
              onClick={() => start("wizard")}
            >
              Ideate use cases
            </Button>
          </div>
        </div>
      )}

      {mode && (
        <>
          {/* The chat is its own bordered surface (like the editor area);
              the tools below sit OUTSIDE it as sibling surfaces with their
              own borders. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border">
            <LayerAiThread turns={turns} isThinking={loading} />
          </div>

          {!loading && shownIdeas.length > 0 && (
            <div className="flex shrink-0 flex-col gap-1.5">
              <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <Icon src="lightbulb-on" size={11} />
                Ideas — click one to continue
              </p>
              <div className="flex flex-wrap gap-1">
                {shownIdeas.map((idea) => (
                  <Button
                    key={idea}
                    variant="outline"
                    size="tiny"
                    className="whitespace-normal !rounded-full text-left font-normal"
                    data-feature-code="UI_BackofficeLayers_AiAssistant_Idea"
                    onClick={() => void send(idea, mode, turns)}
                  >
                    {idea}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Stacked slim cards: two-column wrapping was tried here and
              looked worse — unequal card heights stretch to the tallest
              row and read as whitespace. Compact internals + collapsed-
              by-default history keep the stack short instead. */}
          {identity && onIdentity && (
            <LayerAiIdentityCard
              identity={identity}
              onApply={(proposal) => {
                onIdentity(proposal)
                setIdentity(null)
              }}
              onDismiss={() => setIdentity(null)}
            />
          )}

          <LayerAiSuggestionPanel
            suggestions={suggestions}
            changeSummary={changeSummary}
            fork={fork}
            featureCode={featureCode}
            layerName={layerName}
            draft={draft}
            blobs={blobs}
            mode={mode}
            turns={turns}
            isSavingField={isSavingField}
            onApply={onApply}
            onSaveField={onSaveField}
            onRequestFork={setFork}
            onDismissFork={() => setFork(null)}
            onForked={() => {
              setSuggestions([])
              setFork(null)
            }}
            onHistoryNavigated={onHistoryNavigated}
          />

          <LayerAiComposer
            isSending={loading}
            hasSuggestions={suggestions.length > 0}
            onSend={(text) => void send(text, mode, turns)}
          />
        </>
      )}
    </section>
  )
}

export default LayerAiAssistant
